import React from 'react';

import type { BackendTypes } from '@tf/api';
import type { FilterItem } from '@tf/ui';
import {
	Avatar,
	Box,
	Center,
	createStyles,
	FilterButton,
	Flex,
	Icon,
	SearchInput,
	Stack,
	TFLogo,
	TFText,
	Timeline,
	Title,
	Tooltip,
} from '@tf/ui';
import { fmt, S } from '@tf/utils';

import type { CaseRequestStatus } from '@/core/types/cases';
import { getDisplayName } from '@/core/utils';

const useStyles = createStyles(() => ({
	timelineItem: {
		'& > :not(:first-of-type)': {
			marginTop: '20px !important',
		},
		'& > ::before': {
			bottom: 'calc(20px * -1) !important',
		},
	},
}));

export type Event = {
	id: string;
	kind: string;
	meta: {
		'@author': {
			'@id': string;
			'@kind': string;
		};
		'@createdAt': string;
		case_request_status?: CaseRequestStatus;
	};
};

interface Props {
	searchValue: string;
	setSearchValue: (value: string) => void;
	filters?: { id: string; value: unknown }[];
	setFilters?: React.Dispatch<React.SetStateAction<{ id: string; value: unknown }[]>>;
	availableFilters?: FilterItem[];
	events: Event[];
	listUsers: BackendTypes.UserInfo[];
	messagesMap?: Record<string, (event: Event) => string>;
}

export const BaseEventLog = ({
	searchValue,
	setSearchValue,
	filters,
	setFilters,
	availableFilters = [],
	events,
	listUsers,
	messagesMap,
}: Props) => {
	const { classes } = useStyles();

	return (
		<Box p={'1rem'}>
			<Box mb=".75rem">
				<Flex m="0 0 1.5rem">
					<SearchInput searchValue={searchValue} onSearchChange={(v) => setSearchValue(v)} />
					{setFilters && filters && (
						<FilterButton
							popoverPosition="top-end"
							availableFilters={availableFilters as FilterItem[]}
							activeFilters={filters}
							setFilters={setFilters}
						/>
					)}
				</Flex>
			</Box>

			<Timeline
				className={classes.timelineItem}
				bulletSize={28}
				lineWidth={2}
				styles={{ itemBody: { paddingLeft: '12px !important' } }}
			>
				{!events.length && (
					<Center h={300}>
						<Stack align="center" gap="sm">
							<Title order={4} c="dimmed">
								No events found
							</Title>
						</Stack>
					</Center>
				)}

				{events.map((event, index) => {
					const { icon, name, userExists } = getTimelineData(
						listUsers,
						event.meta['@author']['@id'],
						event.meta['@author']['@kind'],
						event.meta?.case_request_status
					);
					return (
						<Timeline.Item
							key={index}
							bullet={icon}
							title={
								<TFText lh="normal" size="14px" fw="500">
									{name}
									<TFText lh="normal" component="span" c="#6B7280" m="0 4px" size="14px">
										{messagesMap?.[event.kind]?.(event) || S.prettify(event.kind)}
									</TFText>
									{!userExists && (
										<Box display="inline-block" style={{ verticalAlign: 'middle' }}>
											<Tooltip
												label="User information for this event isn’t currently available"
												position="top"
												withArrow
											>
												<Icon.IconInfoSquareRoundedFilled
													color="red"
													style={{ fillRule: 'initial', color: '#6B7280' }}
													size="18"
												/>
											</Tooltip>
										</Box>
									)}
								</TFText>
							}
						>
							<TFText size="14px" sx={{ pl: '20rem' }} c="#6B7280">
								{fmt.toDate(event.meta['@createdAt'], { preset: 'full_date_with_time' })}
							</TFText>
						</Timeline.Item>
					);
				})}
			</Timeline>
		</Box>
	);
};

const getTimelineData = (
	users: BackendTypes.UserInfo[],
	authorId?: string,
	authorKind?: string,
	status?: CaseRequestStatus
) => {
	if (status === 'VIEWED') {
		return {
			icon: (
				<Avatar radius="xl" size="28" color="brand">
					<Icon.IconUser size="18" />
				</Avatar>
			),
			name: 'End client',
			userExists: true,
		};
	}

	if (authorKind === 'API') {
		return {
			icon: (
				<Avatar radius="xl" size="28" styles={{ root: { backgroundColor: '#000000' } }}>
					<Icon.IconApi size="18" color="white" />
				</Avatar>
			),
			name: 'External system',
			userExists: true,
		};
	}

	if (authorId?.includes('_ROBOT') || authorKind === 'SYSTEM') {
		return {
			icon: (
				<Avatar radius="xl" size="28" styles={{ root: { backgroundColor: '#000000' } }}>
					<TFLogo size={14} variant="white" />
				</Avatar>
			),
			name: 'Trustform',
			userExists: true,
		};
	}

	const user = users.find((u) => u.userId === authorId);
	if (user) {
		const fullName = getDisplayName(user);
		return {
			icon: (
				<Avatar radius="xl" size="28" color="brand">
					{S.abbreviate(fullName)}
				</Avatar>
			),
			name: getDisplayName(user),
			userExists: true,
		};
	}

	return {
		icon: (
			<Avatar radius="xl" size="28" color="brand">
				<Icon.IconSpy size="18" />
			</Avatar>
		),
		name: 'Unknown user',
		userExists: false,
	};
};
